import React, { useState, useRef, useEffect } from 'react';
import { FaCloudArrowUp } from 'react-icons/fa6';
import Button from './atoms/Button';
import { useUploadImageMutation } from '../store/apis/common';
import { toastError } from '../utils/toasts';

interface PhotoUploadProps {
  className?: string;
  imageClassName?: string;
  defaultPreview?: string;
  id?: string;
  onFileUploaded: (url: string) => void;
  onFileSelected?: (hasUnsavedFile: boolean) => void;
  isUploading?: (isUploading: boolean) => void;
}

const PhotoUpload: React.FC<PhotoUploadProps> = ({
  className = '', imageClassName = '', defaultPreview, id, onFileUploaded, onFileSelected, isUploading,
}) => {
  const [preview, setPreview] = useState<string>(defaultPreview || '');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadImage, resultsUploadImage] = useUploadImageMutation();

  useEffect(() => {
    if (isUploading) {
      isUploading(resultsUploadImage.isLoading);
    }
  }, [resultsUploadImage.isLoading]);

  // Process file upload - shared logic for both file select and paste
  const processFile = async (file: File) => {
    if (file.size > 15 * 1024 * 1024) {
      toastError('El tamaño máximo permitido es 15MB');
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toastError('Solo se permiten archivos de imagen');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
    resultsUploadImage.reset();
    onFileSelected?.(true);

    try {
      const result = await uploadImage(file).unwrap();
      onFileUploaded(result);
      onFileSelected?.(false);
    } catch (error) {
      console.error('Error uploading image:', error);
    }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      await processFile(file);
    }
  };

  // Handle paste event
  const handlePaste = async (event: ClipboardEvent) => {
    const items = event.clipboardData?.items;
    if (!items) {
      return;
    }

    // Find the first image item
    const imageItem = Array.from(items).find((item) => item.type.indexOf('image') !== -1);

    if (imageItem) {
      event.preventDefault();
      const file = imageItem.getAsFile();
      if (file) {
        await processFile(file);
      }
    }
  };

  // Attach paste event listener
  useEffect(() => {
    const handlePasteEvent = (event: Event) => {
      handlePaste(event as ClipboardEvent);
    };

    window.addEventListener('paste', handlePasteEvent);

    return () => {
      window.removeEventListener('paste', handlePasteEvent);
    };
  }, [uploadImage, onFileUploaded, onFileSelected]);

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div
      className={`relative cursor-pointer ${className} bg-surface-base rounded-lg`}
      onClick={handleClick}
    >
      <input
        type="file"
        ref={fileInputRef}
        className="hidden"
        accept="image/*"
        onChange={handleFileSelect}
        id={id}
      />

      {preview ? (
        <div className="flex flex-col gap-2 border-2 border-dashed border-border-primary rounded-lg p-2">
          <span className="absolute md:top-6 md:left-6 top-4 left-4 bg-surface-base/80 px-2 py-1 rounded text-text-primary text-xs md:text-sm font-medium shadow-sm">Foto actual, pulsá acá para cambiarla</span>
          <img
            src={preview}
            alt="Preview"
            className={`object-cover rounded-lg w-full ${imageClassName}`}
          />
          {resultsUploadImage.isLoading && (
            <div className="flex justify-center whitespace-nowrap absolute bottom-6 right-6">
              <Button disabled loading>
                <span className="flex items-center gap-2 text-lg">
                  <div className="text-2xl"><FaCloudArrowUp /></div>
                  Subiendo...
                </span>
              </Button>
            </div>
          )}
        </div>
      ) : (
        <div className="w-full h-full min-h-[150px] border-2 border-dashed border-border-primary rounded-lg flex items-center justify-center">
          <div className="text-center p-4">
            <p className="text-sm text-text-tertiary text-center">JPG, PNG, GIF hasta 15MB</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default PhotoUpload;
